import React, { createContext, useContext, useState, useEffect } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import { loginAuthLoginPost, logoutAuthLogoutPost } from '../../client';
import NotificationService from '../notification/NotificationService';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/;SameSite=Strict`;
};
const handleServerLogin = async (token: string, csrfToken: string) => {
  try {
    setCookie('auth_token', token, 1);
    setCookie('csrf_token', csrfToken, 1);
    await loginAuthLoginPost({
      headers: {
        Authorization: `Bearer ${token}`,
        'X-CSRF-Token': csrfToken,
      },
    });
    NotificationService.success('Successfully logged in to server!');
  } catch (error) {
    NotificationService.error('Server login failed!');
    throw error;
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    isAuthenticated,
    loginWithRedirect,
    logout: authLogout,
    getAccessTokenSilently,
    isLoading: auth0IsLoading,
  } = useAuth0();
  const [isLoading, setIsLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState('');
  const [hasHandledRedirect, setHasHandledRedirect] = useState(false);

  // Generate CSRF token on mount
  useEffect(() => {
    const generateCSRFToken = () => {
      const array = new Uint8Array(32);
      window.crypto.getRandomValues(array);
      return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
    };
    setCsrfToken(generateCSRFToken());
  }, []);

  // Cookie helpers

  const getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift();
    }
    return null;
  };

  const deleteCookie = (name: string) => {
    document.cookie = `${name}=; Max-Age=0; path=/;`;
  };

  // Effect to handle authentication changes and server login after auth0 redirect
  useEffect(() => {
    const handleAuth = async () => {
      if (!auth0IsLoading && isAuthenticated && !hasHandledRedirect) {
        setIsLoading(true);
        try {
          // User is authenticated with Auth0, get the token and login to server
          const token = await getAccessTokenSilently();
          await handleServerLogin(token, csrfToken);
          setHasHandledRedirect(true);
        } catch (error) {
          NotificationService.error('Failed to complete authentication process');
        } finally {
          setIsLoading(false);
        }
      } else if (!auth0IsLoading && !isAuthenticated) {
        // User is not authenticated and auth0 has finished loading
        setIsLoading(false);
      }
    };

    handleAuth();
  }, [auth0IsLoading, csrfToken, getAccessTokenSilently, hasHandledRedirect, isAuthenticated]);

  const login = async () => {
    setIsLoading(true);
    try {
      // This will redirect to Auth0, and our useEffect above will handle the redirect back
      await loginWithRedirect();
    } catch (error) {
      NotificationService.error('Login process failed');
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      const token = getCookie('auth_token') || (await getAccessTokenSilently());
      await logoutAuthLogoutPost({
        headers: {
          Authorization: `Bearer ${token}`,
          'X-CSRF-Token': csrfToken,
        },
      });
      deleteCookie('auth_token');
      deleteCookie('csrf_token');
      setHasHandledRedirect(false);
      await authLogout({ logoutParams: { returnTo: window.location.origin } });
      NotificationService.success('Successfully logged out from server!');
    } catch (error) {
      NotificationService.error('Logout failed!');
    } finally {
      setIsLoading(false);
    }
  };

  const getToken = async () => {
    const storedToken = getCookie('auth_token');
    if (storedToken) {
      try {
        await loginAuthLoginPost({
          headers: {
            Authorization: `Bearer ${storedToken}`,
            'X-CSRF-Token': csrfToken,
          },
        });
        return storedToken;
      } catch (error) {
        deleteCookie('auth_token');
        deleteCookie('csrf_token');
        return null;
      }
    }
    return null;
  };

  const value = {
    isAuthenticated,
    isLoading: isLoading || auth0IsLoading,
    login,
    logout,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
